import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/auth.service';
import { SECURITY_CONFIG } from '../config/app.config';
import { isValidPin } from '../utils/security';
import { useLang } from '../contexts/LangContext';

const SetupPinPage: React.FC = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const [alreadyEnabled, setAlreadyEnabled] = useState(false);
  const [step, setStep] = useState<'enter' | 'confirm'>('enter');
  const [pin, setPin] = useState<string[]>(Array(SECURITY_CONFIG.PIN_LENGTH).fill(''));
  const [confirmPin, setConfirmPin] = useState<string[]>(Array(SECURITY_CONFIG.PIN_LENGTH).fill(''));
  const [error, setError] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const confirmInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    authService
      .isPinEnabled()
      .then((enabled) => setAlreadyEnabled(enabled))
      .catch(() => setAlreadyEnabled(false));
  }, []);

  const handlePinChange = (index: number, value: string, isConfirm: boolean = false) => {
    if (!/^\d*$/.test(value)) return;

    const currentPin = isConfirm ? confirmPin : pin;
    const setCurrentPin = isConfirm ? setConfirmPin : setPin;
    const refs = isConfirm ? confirmInputRefs : inputRefs;

    const newPin = [...currentPin];
    newPin[index] = value;
    setCurrentPin(newPin);
    setError('');

    if (value && index < SECURITY_CONFIG.PIN_LENGTH - 1) {
      refs.current[index + 1]?.focus();
    }

    // Check if complete
    if (newPin.every(digit => digit !== '') && index === SECURITY_CONFIG.PIN_LENGTH - 1) {
      if (!isConfirm) {
        // Move to confirm step
        setTimeout(() => {
          setStep('confirm');
          confirmInputRefs.current[0]?.focus();
        }, 300);
      } else {
        // Validate and save
        validateAndSave(pin.join(''), newPin.join(''));
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent, isConfirm: boolean = false) => {
    if (e.key === 'Backspace') {
      const currentPin = isConfirm ? confirmPin : pin;
      const refs = isConfirm ? confirmInputRefs : inputRefs;

      if (!currentPin[index] && index > 0) {
        refs.current[index - 1]?.focus();
      }
    }
  };

  const validateAndSave = async (originalPin: string, confirmedPin: string) => {
    if (originalPin !== confirmedPin) {
      setError(t('pin_pinMismatch'));
      setPin(Array(SECURITY_CONFIG.PIN_LENGTH).fill(''));
      setConfirmPin(Array(SECURITY_CONFIG.PIN_LENGTH).fill(''));
      setStep('enter');
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
      return;
    }

    if (!isValidPin(originalPin)) {
      setError(t('pin_invalidPin'));
      return;
    }

    setSaving(true);
    try {
      await authService.setupPin(originalPin);
      navigate('/settings', { replace: true });
    } catch (error) {
      setError(t('pin_pinSaveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (step === 'confirm') {
      setStep('enter');
      setConfirmPin(Array(SECURITY_CONFIG.PIN_LENGTH).fill(''));
      setError('');
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="grow bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md"
      >
        {/* Header */}
        <div className="flex items-center mb-8">
          <button onClick={handleBack} className="mr-4 text-gray-600 hover:text-gray-800">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              {step === 'enter'
                ? (alreadyEnabled ? t('pin_changePin') : t('pin_setupPin'))
                : t('pin_confirmPin')}
            </h2>
            <p className="text-gray-600 text-sm mt-1">
              {step === 'enter'
                ? t('pin_enterNewPin')
                : t('pin_reenterPin')}
            </p>
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center justify-center mb-8">
          <div className={`w-3 h-3 rounded-full ${step === 'enter' ? 'bg-blue-600' : 'bg-blue-300'}`} />
          <div className={`w-12 h-1 mx-2 ${step === 'confirm' ? 'bg-blue-600' : 'bg-gray-300'}`} />
          <div className={`w-3 h-3 rounded-full ${step === 'confirm' ? 'bg-blue-600' : 'bg-gray-300'}`} />
        </div>

        {/* PIN Input - Enter */}
        {step === 'enter' && (
          <motion.div
            key="enter"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <div className="flex justify-center gap-3 mb-6">
              {pin.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => { inputRefs.current[index] = el; }}
                  type="tel"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handlePinChange(index, e.target.value, false)}
                  onKeyDown={(e) => handleKeyDown(index, e, false)}
                  disabled={saving}
                  className={`
                    w-14 h-14 text-center text-2xl font-bold rounded-lg border-2
                    ${error ? 'border-red-500' : 'border-gray-300'}
                    focus:border-blue-500 focus:outline-none
                    transition-colors
                  `}
                />
              ))}
            </div>
          </motion.div>
        )}

        {/* PIN Input - Confirm */}
        {step === 'confirm' && (
          <motion.div
            key="confirm"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
          >
            <div className="flex justify-center gap-3 mb-6">
              {confirmPin.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => { confirmInputRefs.current[index] = el; }}
                  type="tel"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handlePinChange(index, e.target.value, true)}
                  onKeyDown={(e) => handleKeyDown(index, e, true)}
                  disabled={saving}
                  className={`
                    w-14 h-14 text-center text-2xl font-bold rounded-lg border-2
                    ${error ? 'border-red-500' : 'border-gray-300'}
                    focus:border-blue-500 focus:outline-none
                    transition-colors
                  `}
                />
              ))}
            </div>
          </motion.div>
        )}

        {/* Error Message */}
        {error && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-red-500 text-sm text-center mb-4"
          >
            {error}
          </motion.p>
        )}

        {/* Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
          <p className="text-sm text-blue-800">
            <strong>{t('pin_note')}:</strong>{' '}
            {t('pin_securityNote')}
          </p>
        </div>

        {/* Saving Indicator */}
        {saving && (
          <div className="mt-6 text-center text-gray-600">
            {t('pin_savingPin')}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default SetupPinPage;
